import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Container, Row, Col, Form, Card, Button } from 'react-bootstrap';
import { Auth } from '@aws-amplify/auth';

import { useAlert, useHandleError } from '@/hooks';
import { LoadingButton, OtpInput } from '@/components';
import { ACTION_STATUS, ACTION_TYPES } from '@/lib/models';
import { trackAction, toErrorFields } from '@/lib/utils';
import { ReactComponent as SendIcon } from '@/assets/icons/send-icon.svg';
import { ReactComponent as ConfirmSendIcon } from '@/assets/icons/confirm-send-icon.svg';
import { ReactComponent as ExclamationIcon } from '@/assets/icons/exclamation-icon.svg';

export const ForgotPasswordSubmit = () => {
	const history = useHistory<{ emailAddress?: string }>();
	const handleError = useHandleError();
	const { showAlert } = useAlert();

	const [emailAddressInputValue, setEmailAddressInputValue] = useState('');
	const [codeInputValue, setCodeInputValue] = useState('');
	const [newPasswordInputValue, setNewPasswordCodeInputValue] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [codeMessage, setCodeMessage] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);

	async function handleSendANewCodeButtonClick() {
		try {
			setIsLoading(true);
			setCodeMessage(null);
			await Auth.forgotPassword(emailAddressInputValue);

			trackAction({
				actionType: ACTION_TYPES.ACCOUNT_RESET_NEW_CODE,
				status: ACTION_STATUS.SUCCESS,
				emailAddress: emailAddressInputValue,
			});

			setCodeInputValue('');
			setCodeMessage({ kind: 'success', text: 'A new verification code has been sent to your mail.' });
		} catch (error) {
			trackAction({
				actionType: ACTION_TYPES.ACCOUNT_RESET_NEW_CODE,
				status: ACTION_STATUS.FAILURE,
				emailAddress: emailAddressInputValue,
				...toErrorFields(error),
			});
			setCodeMessage({ kind: 'error', text: (error as Error).message || 'Unable to send a new code.' });
		} finally {
			setIsLoading(false);
		}
	}

	useEffect(() => {
		if (!history.location.state?.emailAddress) {
			return history.replace('/forgot-password');
		}

		setEmailAddressInputValue(history.location.state.emailAddress);
	}, [history]);

	async function handleFormSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();

		try {
			setIsLoading(true);
			setCodeMessage(null);
			await Auth.forgotPasswordSubmit(
				emailAddressInputValue,
				codeInputValue.replace(/\s/g, ''),
				newPasswordInputValue
			);

			trackAction({
				actionType: ACTION_TYPES.ACCOUNT_RESET,
				status: ACTION_STATUS.SUCCESS,
				emailAddress: emailAddressInputValue,
			});

			showAlert({
				variant: 'success',
				children: () => {
					return <p className="mb-0 text-white">Password changed successfully.</p>;
				},
			});

			history.push('/sign-in');
		} catch (error) {
			trackAction({
				actionType: ACTION_TYPES.ACCOUNT_RESET,
				status: ACTION_STATUS.FAILURE,
				emailAddress: emailAddressInputValue,
				...toErrorFields(error),
			});
			handleError(error);
			if (
				(error as any).code === 'CodeMismatchException' ||
				(error as any).name === 'CodeMismatchException' ||
				(error as any).code === 'ExpiredCodeException' ||
				(error as any).name === 'ExpiredCodeException'
			) {
				setCodeMessage({
					kind: 'error',
					text: 'The code is invalid please try again or send a new code',
				});
			} else {
				handleError(error);
			}
			setIsLoading(false);
		}
	}

	return (
		<Container className="py-6">
			<Row className="mb-5">
				<Col md={{ span: 10, offset: 1 }} lg={{ span: 8, offset: 2 }} xl={{ span: 6, offset: 3 }}>
					<h1 className="mb-0">Forgot Password</h1>
				</Col>
			</Row>
			<Row>
				<Col md={{ span: 10, offset: 1 }} lg={{ span: 8, offset: 2 }} xl={{ span: 6, offset: 3 }}>
					<Card>
						<Card.Header>
							<Card.Title>Step 2: Reset Password</Card.Title>
						</Card.Header>
						<Card.Body>
							<Form onSubmit={handleFormSubmit}>
								<Form.Group>
									<Form.Label>Email Address</Form.Label>
									<Form.Control
										className="mb-2"
										type="email"
										value={emailAddressInputValue}
										onChange={(event) => {
											setEmailAddressInputValue(event.currentTarget.value);
										}}
										required
										disabled
									/>
									<p className="text-muted">
										If you did not recieve an email containing a password reset code, please
										double-check you have entered the correct email address.
									</p>
								</Form.Group>
								<Form.Group>
									<Form.Label>Code</Form.Label>
									<div className="d-flex align-items-center" style={{ gap: 24 }}>
										<OtpInput
											value={codeInputValue}
											onChange={(next) => {
												setCodeInputValue(next);
												if (codeMessage) setCodeMessage(null);
											}}
											name="reset-code"
											hasError={codeMessage?.kind === 'error'}
										/>
										<Button
											variant="link"
											onClick={handleSendANewCodeButtonClick}
											className="d-flex align-items-center p-0"
											style={{ gap: 8 }}
											disabled={isLoading || !emailAddressInputValue}
										>
											<SendIcon />
											Send a New Code
										</Button>
									</div>
									{codeMessage && (
										<div
											className={`d-flex align-items-center mt-2 mb-0 ${
												codeMessage.kind === 'error' ? 'text-danger' : 'text-dark'
											}`}
											style={{ gap: 8, fontSize: 14 }}
										>
											{codeMessage.kind === 'error' ? <ExclamationIcon /> : <ConfirmSendIcon />}
											<span>{codeMessage.text}</span>
										</div>
									)}
								</Form.Group>
								<Form.Group>
									<Form.Label>New Password</Form.Label>
									<Form.Control
										type="password"
										value={newPasswordInputValue}
										onChange={(event) => {
											setNewPasswordCodeInputValue(event.currentTarget.value);
										}}
										required
									/>
									<ul className="pt-2 pl-4 small">
										<li>One lowercase letter</li>
										<li>One uppercase letter</li>
										<li>One number</li>
										<li>One special character</li>
										<li>Eight characters minimum</li>
									</ul>
								</Form.Group>
								<div className="text-right">
									<Button
										variant="link"
										className="mr-4"
										onClick={() => {
											history.goBack();
										}}
									>
										Go Back
									</Button>
									<LoadingButton
										isLoading={isLoading}
										type="submit"
										disabled={
											!emailAddressInputValue ||
											codeInputValue.length !== 6 ||
											!newPasswordInputValue
										}
									>
										Reset Password
									</LoadingButton>
								</div>
							</Form>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</Container>
	);
};
